<template>
  <Breadcrumbs :breadcrumbs="breadcrumbs" />

  <DynamicContent
    id="about-shop"
    class="container dynamic-content"
    :html="aboutShopData"
  ></DynamicContent>

  <!-- This shop's folders -->
  <DynamicContent
    id="brochures-shop-content"
    class="container dynamic-content"
    :html="brochuresShopData"
  ></DynamicContent>
  <BrochuresGrid
    :input="{ shopSlug: shopSlug }"
    :pagination="{ limit: 13, offset: 0 }"
    :impression-source="BrochureImpressionSource.Shop"
    :title="
      !brochuresShopData ? $t('shop.shop-brochures', [shopName]) : undefined
    "
    class="mb-2"
  />

  <!-- Comparable shop folders TODO for backend-->
  <DynamicContent
    id="similar-brochures-shop-content"
    class="container dynamic-content"
    :html="similarBrochuresData"
  ></DynamicContent>
  <SimilarBrochuresForShopGrid
    :input="{ shopSlug: shopSlug }"
    :title="
      !similarBrochuresData
        ? $t('shop.comparable-brochures', [shopSlug])
        : undefined
    "
    :tag="'h2'"
  />

  <!-- Ad -->
  <Advertisement :display-at="AdDisplayAt.Shop" class="ad mt-1 mb-1" />

  <!-- Offers -->
  <DynamicContent
    id="offers-shop-content"
    class="container dynamic-content"
    :html="offersShopData"
  ></DynamicContent>
  <OffersGrid
    :pagination="{ limit: 8, offset: 0 }"
    :input="{ shopSlug: shopSlug }"
    :infinite-loading="false"
    :show-load-more="true"
    :tag="'h2'"
    :title="!offersShopData ? $t('shop.shop-offers', [shopName]) : undefined"
  />

  <CollapsibleSeoText
    :logo="data?.shop?.fileUrl || undefined"
    :title="shopName"
    :description="data?.shop?.description || ''"
    :tag="'h2'"
    class="mb-4"
  />

  <!-- Comparable shops TODO for backend -->
  <SimilarShopsForShopGrid
    :input="{
      shopSlug: shopSlug,
    }"
    :pagination="{ limit: 15, offset: 0 }"
    :title="$t('shop.comparable-shops')"
    :tag="'h2'"
    class="mb-4"
  />

  <!-- Comparable offers TODO for backend -->
  <!-- <OffersGrid
    :pagination="{ limit: 8, offset: 0, comparableOffersShopSlug: shopSlug }"
    :title="$t('shop.more-offers', [shopSlug])"
    /> -->
  <ShopFaqBlock :title="faqData.title" :faqs="faqData.faqs" v-if="faqData" />
</template>

<script setup lang="ts">
// This is the /shop/offers route, where we show the offers for a specific shop
import { useWebsitePagePath } from "~/composables/useWebsitePath";
import {
  AdDisplayAt,
  BrochureImpressionSource,
  useShopQuery,
  useWebsitePageQuery,
} from "~/graphql/generated";

// It is a bit unfortunate that it shares a slug with the catch-all route, but that's just the nuxt filesystem routing
false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    "nl-NL": "/[slug]/folder-aanbiedingen",
    fr: "/[slug]/foldre-offres",
    nl: "/[slug]/folder-aanbiedingen",
  },
});

const shopSlug = useRoute().params.slug as string;

// Fetch the shop by slug
const { data } = await useShopQuery({
  variables: {
    shopInput: {
      id: shopSlug,
    },
  },
});
if (!data.value?.shop) createError({ statusCode: 404 });

/// Fetch content for the this shop.
const path = useRoute().path;

const { websitePagePath } = useWebsitePagePath(path);

// Currently the type of `page` gives us errors that contentBlocks doesn't exist (it does), so we sprinkle @ts-expect-error here and there.
const page = await useWebsitePageQuery({
  variables: websitePagePath,
});

const aboutShopData = computed(() => {
  return page.data.value?.websitePage &&
    // @ts-expect-error
    page.data.value?.websitePage.contentBlocks &&
    // @ts-expect-error
    page.data.value?.websitePage.contentBlocks[0]
    ? // @ts-expect-error
      page.data.value?.websitePage.contentBlocks[0]
    : null;
});

const brochuresShopData = computed(() => {
  return page.data.value?.websitePage &&
    // @ts-expect-error
    page.data.value?.websitePage.contentBlocks &&
    // @ts-expect-error
    page.data.value?.websitePage.contentBlocks[1]
    ? // @ts-expect-error
      page.data.value?.websitePage.contentBlocks[1]
    : null;
});

const similarBrochuresData = computed(() => {
  return page.data.value?.websitePage &&
    // @ts-expect-error
    page.data.value?.websitePage.contentBlocks &&
    // @ts-expect-error
    page.data.value?.websitePage.contentBlocks[2]
    ? // @ts-expect-error
      page.data.value?.websitePage.contentBlocks[2]
    : null;
});

const offersShopData = computed(() => {
  return page.data.value?.websitePage &&
    // @ts-expect-error
    page.data.value?.websitePage.contentBlocks &&
    // @ts-expect-error
    page.data.value?.websitePage.contentBlocks[3]
    ? // @ts-expect-error
      page.data.value?.websitePage.contentBlocks[3]
    : null;
});

const faqData = computed<{
  title: string;
  faqs: Array<{ question: string; answer: string }>;
} | null>(() => {
  return page.data.value?.websitePage &&
    // @ts-expect-error
    page.data.value?.websitePage.contentBlocks &&
    // @ts-expect-error
    page.data.value?.websitePage.contentBlocks[5] &&
    // @ts-expect-error
    page.data.value?.websitePage.contentQuestionsAnswers
    ? {
        // @ts-expect-error
        title: page.data.value?.websitePage.contentBlocks[5],
        // @ts-expect-error
        faqs: page.data.value?.websitePage.contentQuestionsAnswers,
      }
    : null;
});

const shopName = computed(() => {
  return data.value?.shop?.name ?? "";
});

// Set the breadcrumbs
const breadcrumbs = [
  {
    to: "shops",
  },
  {
    to: "slug-shops",
    text: shopName.value,
  },
];

// Set the page title
const { t } = useI18n();
useHead({
  title: computed(() => data.value?.shop?.metaTitle || shopName.value),
  meta: [
    {
      name: "description",
      content: computed(
        () =>
          data.value?.shop?.metaDescription ||
          t("shop.shop-brochures", [shopName.value]),
      ),
    },
  ],
});
</script>
